import { useState } from 'react';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';

const schema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(40).optional().or(z.literal('')),
  category: z.enum(['account', 'billing', 'listing', 'agency', 'developer', 'technical', 'abuse_report', 'other']),
  subject: z.string().trim().min(3).max(200),
  message: z.string().trim().min(10).max(4000),
});

const CATEGORIES = [
  { value: 'account', label: 'Account & login' },
  { value: 'billing', label: 'Billing & payments' },
  { value: 'listing', label: 'Property / listing issue' },
  { value: 'agency', label: 'Agency related' },
  { value: 'developer', label: 'Developer related' },
  { value: 'technical', label: 'Technical problem' },
  { value: 'abuse_report', label: 'Report abuse / fraud' },
  { value: 'other', label: 'Other' },
];

interface Props {
  onSubmitted?: (ticketNumber: string) => void;
  defaultCategory?: string;
}

export function ContactSupportForm({ onSubmitted, defaultCategory }: Props) {
  const { user, profile, hasRole } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: profile?.full_name || '',
    email: user?.email || '',
    phone: '',
    category: defaultCategory || 'other',
    subject: '',
    message: '',
  });

  const update = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const firstErr = parsed.error.issues[0];
      toast.error(firstErr?.message || 'Please fill in all required fields correctly');
      return;
    }
    setSubmitting(true);
    try {
      const priority =
        parsed.data.category === 'abuse_report' ? 'high'
        : (user && (hasRole('agency_admin') || hasRole('agent') || hasRole('developer'))) ? 'high'
        : 'normal';

      const { data, error } = await supabase
        .from('support_tickets' as any)
        .insert({
          ...parsed.data,
          phone: parsed.data.phone || null,
          user_id: user?.id ?? null,
          priority,
          source: 'contact_form',
        })
        .select('ticket_number')
        .single();

      if (error) throw error;
      const tn = (data as any)?.ticket_number;

      // Lookup id and add first message
      const { data: t } = await supabase.from('support_tickets' as any).select('id').eq('ticket_number', tn).single();
      if (t) {
        await supabase.from('support_ticket_messages' as any).insert({
          ticket_id: (t as any).id,
          author_id: user?.id ?? null,
          author_name: parsed.data.name,
          author_type: 'customer',
          body: parsed.data.message,
        });
      }

      setTicketNumber(tn);
      onSubmitted?.(tn);
      toast.success(`Ticket ${tn} created — we'll get back to you soon.`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not submit your request');
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketNumber) {
    return (
      <div className="text-center space-y-4 py-6">
        <CheckCircle2 className="h-14 w-14 text-primary mx-auto" />
        <div>
          <h3 className="font-semibold text-lg">Thanks for reaching out!</h3>
          <p className="text-sm text-muted-foreground">Your support ticket has been created.</p>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/5 border border-primary/20">
          <span className="text-xs text-muted-foreground">Reference:</span>
          <span className="font-mono font-bold text-primary">{ticketNumber}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {user
            ? 'You can track this ticket from your dashboard under "Support".'
            : 'Save this reference number — we will respond to your email.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="cs-name">Full name *</Label>
          <Input id="cs-name" value={form.name} onChange={e => update('name', e.target.value)} required maxLength={100} />
        </div>
        <div>
          <Label htmlFor="cs-email">Email *</Label>
          <Input id="cs-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} required maxLength={255} />
        </div>
        <div>
          <Label htmlFor="cs-phone">Phone (optional)</Label>
          <Input id="cs-phone" value={form.phone} onChange={e => update('phone', e.target.value)} maxLength={40} />
        </div>
        <div>
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={v => update('category', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="cs-subject">Subject *</Label>
        <Input id="cs-subject" value={form.subject} onChange={e => update('subject', e.target.value)} required maxLength={200} placeholder="Briefly summarize your issue" />
      </div>
      <div>
        <Label htmlFor="cs-message">Message *</Label>
        <Textarea id="cs-message" value={form.message} onChange={e => update('message', e.target.value)} required maxLength={4000} rows={5} placeholder="Tell us what's happening, including any reference numbers..." />
      </div>
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        {submitting ? 'Submitting...' : 'Submit ticket'}
      </Button>
    </form>
  );
}
